/**
 * Preparacion del texto antes de sintetizarlo.
 *
 * La diferencia entre una voz que suena a persona y una que suena a maquina no
 * esta solo en el motor: esta en lo que se le entrega. Un guion con markdown,
 * simbolos y frases sin entonacion produce lectura plana por muy buena que sea
 * la voz.
 *
 * Lo que se corrige aqui:
 *   - se quitan los restos de markdown, que se leerian en voz alta
 *   - se abren interrogaciones y exclamaciones, que es lo que hace que el
 *     espanol suba la entonacion desde el principio de la frase y no al final
 *   - se convierten simbolos y abreviaturas en palabras
 *   - se sustituyen los puntos suspensivos por una pausa que el motor entiende
 */

/** Simbolos y abreviaturas que deben leerse como palabras. */
const REPLACEMENTS: Array<[RegExp, string]> = [
  [/\bUMET\b/g, 'Umet'],
  [/\bCEC\b/g, 'Cec'],
  [/\bN\.?º\s*/gi, 'numero '],
  [/\bNro\.?\s*/gi, 'numero '],
  [/\bDr\.\s*/g, 'Doctor '],
  [/\bDra\.\s*/g, 'Doctora '],
  [/\bIng\.\s*/g, 'Ingeniero '],
  [/\bLic\.\s*/g, 'Licenciado '],
  [/\bSr\.\s*/g, 'Senor '],
  [/\bSra\.\s*/g, 'Senora '],
  [/\betc\./gi, 'etcetera'],
  [/\bp\.\s?ej\./gi, 'por ejemplo'],
  [/\bEE\.?\s?UU\.?/g, 'Estados Unidos'],
  [/%/g, ' por ciento'],
  [/&/g, ' y '],
  [/\+/g, ' mas '],
  [/\bUSD\b/g, 'dolares'],
  [/\bhrs?\b/gi, 'horas'],
];

/** Emojis y simbolos pictograficos: no se pronuncian, se eliminan. */
const PICTOGRAPHS =
  /[\u{1F300}-\u{1FAFF}\u{2600}-\u{27BF}\u{FE0F}\u{2190}-\u{21FF}\u{2B00}-\u{2BFF}]/gu;

/**
 * Abre la interrogacion o la exclamacion si el modelo las omitio.
 *
 * En espanol el signo de apertura no es ortografia decorativa: es lo que indica
 * al sintetizador donde empieza la curva de entonacion. Sin el, una pregunta se
 * lee como una afirmacion y suena inmediatamente artificial.
 */
function openPunctuation(sentence: string): string {
  const trimmed = sentence.trim();
  if (!trimmed) return trimmed;

  if (trimmed.endsWith('?') && !trimmed.includes('¿')) {
    // Si la pregunta va precedida de una introduccion, se abre solo la pregunta.
    const pivot = Math.max(trimmed.lastIndexOf(', '), trimmed.lastIndexOf(': '));
    if (pivot > 0) {
      return `${trimmed.slice(0, pivot + 2)}¿${trimmed.slice(pivot + 2)}`;
    }
    return `¿${trimmed}`;
  }

  if (trimmed.endsWith('!') && !trimmed.includes('¡')) {
    return `¡${trimmed}`;
  }

  return trimmed;
}

/** Convierte un fragmento de respuesta en algo que se pueda leer en voz alta. */
export function forSpeech(input: string): string {
  let text = input;

  // Restos de markdown: se leerian como "asterisco", "almohadilla"...
  text = text.replace(/```[\s\S]*?```/g, ' ');
  text = text.replace(/`([^`]*)`/g, '$1');
  text = text.replace(/\*\*([^*]*)\*\*/g, '$1');
  text = text.replace(/\*([^*]*)\*/g, '$1');
  text = text.replace(/^#{1,6}\s*/gm, '');
  text = text.replace(/^\s*[-*•]\s+/gm, '');
  text = text.replace(/\[([^\]]*)\]\([^)]*\)/g, '$1');

  // Direcciones web: se anuncian, no se deletrean.
  text = text.replace(/https?:\/\/\S+/g, 'el sitio web de la universidad');

  text = text.replace(PICTOGRAPHS, '');

  for (const [pattern, replacement] of REPLACEMENTS) {
    text = text.replace(pattern, replacement);
  }

  // Puntos suspensivos y guiones largos: pausa, no silencio raro.
  text = text.replace(/…/g, ', ');
  text = text.replace(/\.{3,}/g, ', ');
  text = text.replace(/\s[—–-]\s/g, ', ');

  text = text.replace(/\s+/g, ' ').trim();

  return openPunctuation(text);
}
